/**
 * MapLibre map: OSM raster basemap (no API key), the radar reflectivity image overlay, and the
 * hail-event polygon layer. This module owns all MapLibre objects; callers interact through the
 * small ``HailMap`` facade (set overlay, set polygons, toggle radar, fit) so the rest of the app
 * never touches raw MapLibre source/layer IDs.
 */

import maplibregl, { type GeoJSONSource, type MapGeoJSONFeature } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type {
  HailEventCollection,
  Lead,
  PartnerAlert,
  PredictedSwath,
  RadarOverlay,
} from './api.ts';
import { escapeHtml, googleMapsHref, safeHttpUrl } from './dom.ts';

const RADAR_SOURCE = 'radar-overlay';
const RADAR_LAYER = 'radar-overlay-layer';
const HAIL_SOURCE = 'hail-events';
const HAIL_FILL = 'hail-events-fill';
const HAIL_OUTLINE = 'hail-events-outline';
const HAIL_OUTLINE_UNDERESTIMATED = 'hail-events-outline-underestimated';
const FORECAST_SOURCE = 'forecast-swaths';
const FORECAST_FILL = 'forecast-swaths-fill';
const FORECAST_OUTLINE = 'forecast-swaths-outline';
const SHOPS_SOURCE = 'matched-shops';
const SHOPS_LAYER = 'matched-shops-circle';

// Partner (consented) vs lead (public) get distinct colours so the map reads at a glance.
const PARTNER_COLOR = '#1d4ed8'; // blue
const LEAD_COLOR = '#f97316'; // orange

const EMPTY_SHOPS: GeoJSON.FeatureCollection<GeoJSON.Point> = {
  type: 'FeatureCollection',
  features: [],
};

// Czech Republic centre + a zoom that frames the whole country on a typical screen.
const CZ_CENTER: [number, number] = [15.6, 49.8];
const CZ_ZOOM = 6.4;
// Keep the view on CZ + its borderlands: the map cannot be panned away to the rest of the world and
// cannot be zoomed out past the whole country. The box is generous (CZ plus a strip of the
// neighbouring border regions) so the full-country overview at CZ_ZOOM still fits inside it.
const CZ_MAX_BOUNDS: [[number, number], [number, number]] = [
  [10.0, 47.5], // SW (lon, lat)
  [20.5, 52.0], // NE
];
const CZ_MIN_ZOOM = 6;

const EMPTY_FC: HailEventCollection = { type: 'FeatureCollection', features: [], metadata: {} };

const EMPTY_FORECAST: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
  type: 'FeatureCollection',
  features: [],
};

// Confidence -> outline opacity. The predicted swaths are drawn faded + dashed so they never read as
// the (solid red) CURRENT swath; lower confidence (further-out lead time) fades more. The colour is a
// neutral violet, distinct from the danger-red current swath and the partner/lead shop dots.
const FORECAST_COLOR = '#7c3aed'; // violet
const FORECAST_OPACITY: Record<PredictedSwath['confidence'], number> = {
  high: 0.9,
  medium: 0.6,
  low: 0.35,
};

// OSM raster style built inline (no external style JSON, no key). Attribution is required by the
// OSM tile usage policy.
const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

// Flat, JSON-primitive shop properties — MapLibre GeoJSON feature properties only round-trip
// primitives reliably, so we keep contact fields as top-level strings (omitted when absent rather
// than set to undefined, to satisfy exactOptionalPropertyTypes). `key` is the cross-module focus
// handle: 'partner:<shop_id>' or 'lead:<place_id>' (Places leads have no numeric id — and no name:
// EEA terms allow only the pin + our deep-link on a non-Google map).
interface ShopProps {
  key: string;
  kind: 'partner' | 'lead';
  distance_km: number;
  name?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  maps_url?: string;
  /** Lead only: OUR derived trade label (displayable, unlike Places content). */
  category?: string;
}

// Flat, JSON-primitive properties for a predicted swath polygon — the paint expressions read
// `confidence` and `lead_min` off the feature to fade/label each lead time differently.
interface ForecastProps {
  lead_min: number;
  confidence: PredictedSwath['confidence'];
}

function forecastFeature(swath: PredictedSwath): GeoJSON.Feature<GeoJSON.Polygon, ForecastProps> {
  return {
    type: 'Feature',
    geometry: swath.geometry,
    properties: { lead_min: swath.lead_min, confidence: swath.confidence },
  };
}

function partnerFeature(shop: PartnerAlert): GeoJSON.Feature<GeoJSON.Point, ShopProps> {
  const properties: ShopProps = {
    key: `partner:${shop.shop_id}`,
    name: shop.name,
    kind: 'partner',
    distance_km: shop.distance_km,
  };
  if (shop.website !== null) properties.website = shop.website;
  if (shop.contact_email !== null) properties.contact_email = shop.contact_email;
  if (shop.contact_phone !== null) properties.contact_phone = shop.contact_phone;
  return { type: 'Feature', geometry: shop.geometry, properties };
}

/** A Places LEAD marker of a CONFIRMED event: pin + deep-link ONLY. EEA terms forbid showing any
 * Places content (name, contacts) on our map beyond lat/lng + place_id, so its popup is a single
 * "Vizitka (Google)" link to the full business card (§7 opt-in — leads are never auto-contacted). */
function leadFeature(shop: Lead): GeoJSON.Feature<GeoJSON.Point, ShopProps> {
  return {
    type: 'Feature',
    geometry: shop.geometry,
    properties: {
      key: `lead:${shop.place_id}`,
      kind: 'lead',
      distance_km: shop.distance_km,
      maps_url: shop.maps_url,
      category: shop.category,
    },
  };
}

/**
 * Popup body for a shop. All shop-derived text is HTML-escaped; the map deep-links carry no API key
 * and store nothing. A partner popup offers its consented mailto:/tel: links; a Places lead popup is
 * a neutral "Servis (Google)" with ONLY the business-card deep-link (EEA: no Places content on our
 * map). No outreach message is shown for anyone.
 */
function shopPopupHtml(props: ShopProps, lng: number, lat: number): string {
  const distance = `${props.distance_km.toFixed(1)} km`;
  if (props.kind === 'lead') {
    const vizitka = props.maps_url !== undefined ? safeHttpUrl(props.maps_url) : null;
    const link =
      vizitka !== null
        ? `<a href="${escapeHtml(vizitka)}" target="_blank" rel="noopener">Vizitka (Google)</a>`
        : '';
    return `
      <div class="shop-popup">
        <strong class="shop-popup-name">${escapeHtml(props.category ?? 'Servis')}</strong>
        <span class="shop-badge shop-badge-lead">Google</span>
        <span class="shop-popup-dist">${escapeHtml(distance)}</span>
        <div class="shop-popup-links">${link}</div>
      </div>`;
  }

  const name = props.name ?? '';
  // Open the partner's Google Maps place page by name at its coordinates (a nameless row degrades
  // to a coordinate pin).
  const mapsUrl = googleMapsHref(name, lng, lat);
  const links: string[] = [];
  const website = props.website !== undefined ? safeHttpUrl(props.website) : null;
  if (website !== null) {
    links.push(`<a href="${escapeHtml(website)}" target="_blank" rel="noopener">Web</a>`);
  }
  links.push(`<a href="${mapsUrl}" target="_blank" rel="noopener">Mapa</a>`);
  if (props.contact_email !== undefined && props.contact_email.length > 0) {
    links.push(`<a href="mailto:${encodeURIComponent(props.contact_email)}">E-mail</a>`);
  }
  if (props.contact_phone !== undefined && props.contact_phone.length > 0) {
    links.push(`<a href="tel:${encodeURIComponent(props.contact_phone)}">Telefon</a>`);
  }

  return `
    <div class="shop-popup">
      <strong class="shop-popup-name">${escapeHtml(name)}</strong>
      <span class="shop-badge shop-badge-partner">Partner</span>
      <span class="shop-popup-dist">${escapeHtml(distance)}</span>
      <div class="shop-popup-links">${links.join('')}</div>
    </div>`;
}

export class HailMap {
  private readonly map: maplibregl.Map;
  private ready = false;
  // Last overlay image applied, so a periodic refresh that yields the same scan does not re-issue
  // updateImage — re-issuing aborts MapLibre's in-flight image load and logs a noisy AbortError.
  private radarImageUri: string | null = null;
  // Current event's shop features, keyed by the string focus key ('partner:<id>' / 'lead:<place_id>'),
  // so the sidebar can focus one row's pin.
  private shopFeatures = new Map<string, GeoJSON.Feature<GeoJSON.Point, ShopProps>>();
  // At most one shop popup is open at a time; reused so clicking another shop replaces it.
  private shopPopup: maplibregl.Popup | null = null;

  constructor(container: HTMLElement, onPolygonClick: (id: number) => void) {
    this.map = new maplibregl.Map({
      container,
      style: OSM_STYLE,
      center: CZ_CENTER,
      zoom: CZ_ZOOM,
      minZoom: CZ_MIN_ZOOM,
      maxBounds: CZ_MAX_BOUNDS,
      attributionControl: { compact: true },
    });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    this.map.on('load', () => {
      this.initLayers();
      this.ready = true;
    });
    this.bindClicks(onPolygonClick);
  }

  private initLayers(): void {
    // Radar image source seeded with a 1x1 transparent pixel over the CZ-ish box; replaced once the
    // real overlay arrives. A placeholder is needed because addLayer requires its source to exist.
    this.map.addSource(RADAR_SOURCE, {
      type: 'image',
      url: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      coordinates: [
        [12.0, 51.1],
        [19.0, 51.1],
        [19.0, 48.5],
        [12.0, 48.5],
      ],
    });
    this.map.addLayer({
      id: RADAR_LAYER,
      type: 'raster',
      source: RADAR_SOURCE,
      paint: { 'raster-opacity': 0.8, 'raster-fade-duration': 0 },
      layout: { visibility: 'none' },
    });

    this.map.addSource(HAIL_SOURCE, { type: 'geojson', data: EMPTY_FC });
    this.map.addLayer({
      id: HAIL_FILL,
      type: 'fill',
      source: HAIL_SOURCE,
      paint: { 'fill-color': '#dc2626', 'fill-opacity': 0.35 },
    });
    // Base outline for every swath. The colour IS data-driven (amber for an underestimated swath, so
    // it reads as "review, size uncertain"; dark-red otherwise) — 'line-color' is a data-driven
    // property so a ['case', ['get', ...]] expression is valid. The DASH, however, cannot be: in
    // MapLibre v4 'line-dasharray' is a cross-faded property whose only expression parameter is 'zoom'
    // (a data ['get', ...] there throws "data expressions not supported" at addLayer). So the dashed
    // distinction lives on the dedicated, statically-dashed layer below — mirroring FORECAST_OUTLINE.
    this.map.addLayer({
      id: HAIL_OUTLINE,
      type: 'line',
      source: HAIL_SOURCE,
      paint: {
        'line-color': ['case', ['get', 'size_underestimated'], '#d97706', '#991b1b'],
        'line-width': 2,
      },
    });
    // The amber DASH for underestimated swaths only, drawn over the base outline. A separate layer with
    // a feature FILTER + a static 'line-dasharray' is the supported way to get a data-conditional dash
    // (the property itself rejects data expressions). The fill stays red — it is still a real swath.
    this.map.addLayer({
      id: HAIL_OUTLINE_UNDERESTIMATED,
      type: 'line',
      source: HAIL_SOURCE,
      filter: ['==', ['get', 'size_underestimated'], true],
      paint: {
        'line-color': '#d97706',
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    });

    // Predicted future swaths (F-4B): the CURRENT swath translated forward to +15/+30/+45 min.
    // Drawn faded + dashed (and fading further with lower confidence) so they read clearly as a
    // forecast, never confused with the solid-red current swath. MOVEMENT only — see the sidebar
    // caveat; the map deliberately does not imply an intensity change.
    this.map.addSource(FORECAST_SOURCE, { type: 'geojson', data: EMPTY_FORECAST });
    this.map.addLayer({
      id: FORECAST_FILL,
      type: 'fill',
      source: FORECAST_SOURCE,
      paint: {
        'fill-color': FORECAST_COLOR,
        'fill-opacity': [
          'match',
          ['get', 'confidence'],
          'high',
          0.18,
          'medium',
          0.12,
          0.06, // low (and any default)
        ],
      },
    });
    this.map.addLayer({
      id: FORECAST_OUTLINE,
      type: 'line',
      source: FORECAST_SOURCE,
      paint: {
        'line-color': FORECAST_COLOR,
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': [
          'match',
          ['get', 'confidence'],
          'high',
          FORECAST_OPACITY.high,
          'medium',
          FORECAST_OPACITY.medium,
          FORECAST_OPACITY.low, // low (and any default)
        ],
      },
    });

    // Matched shops on top of the swath. Colour encodes the consent split (partner vs lead).
    this.map.addSource(SHOPS_SOURCE, { type: 'geojson', data: EMPTY_SHOPS });
    this.map.addLayer({
      id: SHOPS_LAYER,
      type: 'circle',
      source: SHOPS_SOURCE,
      paint: {
        'circle-radius': 7,
        'circle-color': [
          'match',
          ['get', 'kind'],
          'partner',
          PARTNER_COLOR,
          LEAD_COLOR, // default = public/lead
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }

  private bindClicks(onPolygonClick: (id: number) => void): void {
    this.map.on('click', HAIL_FILL, (e) => {
      const feature: MapGeoJSONFeature | undefined = e.features?.[0];
      const id: unknown = feature?.properties['event_id'];
      if (typeof id === 'number') onPolygonClick(id);
    });
    this.map.on('mouseenter', HAIL_FILL, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', HAIL_FILL, () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('click', SHOPS_LAYER, (e) => {
      const feature: MapGeoJSONFeature | undefined = e.features?.[0];
      const key: unknown = feature?.properties['key'];
      if (typeof key === 'string') this.openShopPopup(key);
    });
    this.map.on('mouseenter', SHOPS_LAYER, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', SHOPS_LAYER, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  /** Open (or replace) the popup for one shop by key. No-op if it is not in the current event. */
  private openShopPopup(shopKey: string): void {
    const feature = this.shopFeatures.get(shopKey);
    if (feature === undefined) return;
    const [lng, lat] = feature.geometry.coordinates;
    if (lng === undefined || lat === undefined) return;
    this.shopPopup?.remove();
    this.shopPopup = new maplibregl.Popup({ offset: 10, closeButton: true })
      .setLngLat([lng, lat])
      .setHTML(shopPopupHtml(feature.properties, lng, lat))
      .addTo(this.map);
  }

  /** Run ``fn`` once the style has loaded (immediately if it already has). */
  private whenReady(fn: () => void): void {
    if (this.ready) fn();
    // once() has a Promise-returning overload (no-listener form); we pass a listener, so void the
    // chainable Map return to satisfy no-floating-promises.
    else void this.map.once('load', fn);
  }

  /** Replace the radar image source with the latest overlay (or clear it when null). */
  setRadarOverlay(overlay: RadarOverlay | null): void {
    this.whenReady(() => {
      const source = this.map.getSource(RADAR_SOURCE);
      if (!(source instanceof maplibregl.ImageSource)) return;
      if (overlay === null) {
        this.radarImageUri = null;
        this.map.setLayoutProperty(RADAR_LAYER, 'visibility', 'none');
        return;
      }
      // Same scan as last time → nothing to update; avoids aborting the in-flight image load.
      if (overlay.image_data_uri === this.radarImageUri) return;
      this.radarImageUri = overlay.image_data_uri;
      const [west, south, east, north] = overlay.bounds;
      source.updateImage({
        url: overlay.image_data_uri,
        // ImageSource corners, clockwise from top-left: NW, NE, SE, SW.
        coordinates: [
          [west, north],
          [east, north],
          [east, south],
          [west, south],
        ],
      });
    });
  }

  toggleRadar(visible: boolean): void {
    this.whenReady(() => {
      this.map.setLayoutProperty(RADAR_LAYER, 'visibility', visible ? 'visible' : 'none');
    });
  }

  /** Replace the hail polygons. Each feature must carry ``properties.event_id`` for click routing. */
  setHailEvents(collection: HailEventCollection): void {
    this.whenReady(() => {
      const source = this.map.getSource(HAIL_SOURCE);
      if (source instanceof Object && 'setData' in source) {
        (source as GeoJSONSource).setData(collection);
      }
    });
  }

  /**
   * Replace the markers for the selected event: contracted partners and public leads (F-4C). Partner
   * popups offer mailto:/tel: manual-contact links; lead popups only Web/Mapa (no message for anyone).
   * Pass empty arrays (or call on deselect) to clear them — that also dismisses any open shop popup.
   */
  setShops(partnerAlerts: PartnerAlert[], leads: Lead[]): void {
    const features: Array<[string, GeoJSON.Feature<GeoJSON.Point, ShopProps>]> = [
      ...partnerAlerts.map((s): [string, GeoJSON.Feature<GeoJSON.Point, ShopProps>] => [
        `partner:${s.shop_id}`,
        partnerFeature(s),
      ]),
      ...leads.map((s): [string, GeoJSON.Feature<GeoJSON.Point, ShopProps>] => [
        `lead:${s.place_id}`,
        leadFeature(s),
      ]),
    ];
    this.shopFeatures = new Map(features);
    if (features.length === 0) {
      this.shopPopup?.remove();
      this.shopPopup = null;
    }
    this.whenReady(() => {
      const source = this.map.getSource(SHOPS_SOURCE);
      if (source instanceof Object && 'setData' in source) {
        (source as GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [...this.shopFeatures.values()],
        });
      }
    });
  }

  /**
   * Replace the predicted future-swath outlines for the selected event. Pass an empty array (or call
   * on deselect) to clear them — a new event with no movement vector yet has no predicted swaths.
   */
  setForecast(swaths: PredictedSwath[]): void {
    this.whenReady(() => {
      const source = this.map.getSource(FORECAST_SOURCE);
      if (source instanceof Object && 'setData' in source) {
        (source as GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: swaths.map(forecastFeature),
        });
      }
    });
  }

  /** Fly to a shop and open its popup (used when a shop is picked from the sidebar list). */
  focusShop(shopKey: string): void {
    const feature = this.shopFeatures.get(shopKey);
    if (feature === undefined) return;
    const [lng, lat] = feature.geometry.coordinates;
    if (lng === undefined || lat === undefined) return;
    this.whenReady(() => {
      this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 11), duration: 600 });
      this.openShopPopup(shopKey);
    });
  }

  /** Pan/zoom to the given EPSG:4326 box [west, south, east, north]. */
  fitBounds(bounds: [number, number, number, number]): void {
    this.whenReady(() => {
      this.map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        // Cap the zoom so picking an event frames it WITH its regional context (most of CZ stays in
        // view) instead of diving to street level — the country, not one storm, is the main subject.
        { padding: 48, maxZoom: 8.5, duration: 600 },
      );
    });
  }

  /** Frame a single swath polygon (used when the user picks an event from history/the map). */
  fitToPolygon(geometry: GeoJSON.Polygon): void {
    const bounds = polygonBounds(geometry);
    if (bounds !== null) this.fitBounds(bounds);
  }
}

/**
 * EPSG:4326 [west, south, east, north] enclosing a GeoJSON polygon's exterior ring, or null when
 * the ring has no coordinates (a malformed event we should not try to zoom to).
 */
function polygonBounds(geometry: GeoJSON.Polygon): [number, number, number, number] | null {
  const ring = geometry.coordinates[0];
  if (ring === undefined || ring.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const position of ring) {
    // GeoJSON Position is typed as number[]; guard the index access under noUncheckedIndexedAccess.
    const lng = position[0];
    const lat = position[1];
    if (lng === undefined || lat === undefined) continue;
    if (lng < west) west = lng;
    if (lng > east) east = lng;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }
  if (west === Infinity) return null;
  return [west, south, east, north];
}
